import { required, maxLength, numeric, minLength } from 'vuelidate/lib/validators'

import usStates from '@/data/us-states'
import canadaStates from '@/data/canada-states'
import settings from '@/settings'
import FormValidationMixin from '@/mixins/FormValidationMixin'
import { LOCALE } from '@/helpers/locale'
import { mapGetters } from 'vuex'

export default {
  mixins: [FormValidationMixin],
  computed: {
    ...mapGetters('locale', ['getLocaleCode']),
    isFrenchLocale () {
      return this.getLocaleCode === LOCALE['fr-ca'].code
    }
  },
  watch: {
    isFrenchLocale: {
      handler (newValue) {
        if (newValue) {
          this.form.state.titleField = 'nameFr'
        }
      },
      immediate: true
    }
  },
  methods: {
    setAddressFormToDefault () {
      Object.values(this.form).forEach((field) => {
        if (typeof field.defaultValue === 'object' && field.defaultValue !== null) {
          field.value = { ...field.defaultValue }
        } else {
          field.value = field.defaultValue
        }
      })
    },
    isTwoAddressesEqualForValidation (firstAddress, secondAddress) {
      const parseAddressForValidation = (addressObject) => {
        const fieldsForValidation = {
          line1: addressObject.line1,
          line2: addressObject.line2,
          postalCode: addressObject.postalCode,
          region: addressObject.region?.isocode,
          town: addressObject.town
        }
        for (const key in fieldsForValidation) {
          if (!fieldsForValidation[key]) delete fieldsForValidation[key]
        }
        return { ...fieldsForValidation }
      }

      return this.isEqualObjects(
        parseAddressForValidation(firstAddress),
        parseAddressForValidation(secondAddress)
      )
    },
    prepareFields () {
      switch (settings.app.country) {
        case 'CA':
          this.form.country.value = { isocode: 'CA', name: 'Canada' }
          this.form.country.defaultValue = { isocode: 'CA', name: 'Canada' }
          this.form.state.options = canadaStates
          this.form.state.defaultValue = canadaStates
          break
      }
    }
  },
  created () {
    this.prepareFields()
  },
  validations () {
    return {
      form: {
        firstName: {
          value: {
            required,
            maxLength: maxLength(35),
            server: this.server
          }
        },
        lastName: {
          value: {
            required,
            maxLength: maxLength(35),
            server: this.server
          }
        },
        contact1: {
          value: {
            required,
            maxLength: maxLength(35),
            server: this.server
          }
        },
        contact2: {
          value: {
            maxLength: maxLength(35),
            server: this.server
          }
        },
        city: {
          value: {
            required,
            maxLength: maxLength(35),
            server: this.server
          }
        },
        state: {
          value: {
            required,
            server: this.server
          }
        },
        postalCode: {
          value: {
            required,
            maxLength: maxLength(10),
            server: this.server
          }
        },
        phone: {
          value: {
            required,
            numeric,
            maxLength: maxLength(10),
            minLength: minLength(10),
            server: this.server
          }
        }
      }
    }
  },
  data () {
    return {
      isValidationRun: false,
      form: {
        id: {
          title: 'id',
          value: '',
          defaultValue: ''
        },
        defaultAddress: {
          title: 'defaultAddress',
          value: false,
          defaultValue: false
        },
        firstName: {
          placeholder: this.$t('account.addressBook.form.firstName.placeholder'),
          value: '',
          defaultValue: '',
          title: 'firstName',
          type: 'text',
          variant: 'shadow',
          maxLength: '35',
          inline: true,
          errors: {
            required: this.$t('account.addressBook.form.firstName.requiredError'),
            minLength: this.$t('account.addressBook.form.firstName.minLengthError')
          },
          disabled: false
        },
        lastName: {
          placeholder: this.$t('account.addressBook.form.lastName.placeholder'),
          value: '',
          defaultValue: '',
          title: 'lastName',
          type: 'text',
          variant: 'shadow',
          maxLength: '35',
          inline: true,
          errors: {
            required: this.$t('account.addressBook.form.lastName.requiredError'),
            minLength: this.$t('account.addressBook.form.lastName.minLengthError')
          },
          disabled: false
        },
        companyName: {
          placeholder: this.$t('account.addressBook.form.companyName.placeholder'),
          value: '',
          defaultValue: '',
          title: 'companyName',
          variant: 'shadow',
          type: 'text',
          maxLength: '35',
          disabled: false
        },
        contact1: {
          placeholder: this.$t('account.addressBook.form.contact1.placeholder'),
          value: '',
          defaultValue: '',
          title: 'line1',
          type: 'text',
          variant: 'shadow',
          maxLength: '35',
          errors: {
            required: this.$t('account.addressBook.form.contact1.requiredError'),
            minLength: this.$t('account.addressBook.form.contact1.minLengthError')
          },
          disabled: false
        },
        contact2: {
          placeholder: this.$t('account.addressBook.form.contact2.placeholder'),
          value: '',
          defaultValue: '',
          title: 'line2',
          variant: 'shadow',
          type: 'text',
          maxLength: '35',
          errors: {},
          disabled: false
        },
        city: {
          placeholder: this.$t('account.addressBook.form.city.placeholder'),
          value: '',
          defaultValue: '',
          title: 'town',
          type: 'text',
          maxLength: '35',
          variant: 'shadow',
          inline: true,
          errors: {
            required: this.$t('account.addressBook.form.city.requiredError'),
            minLength: this.$t('account.addressBook.form.city.minLengthError')
          },
          disabled: false
        },
        state: {
          placeholder: this.$t('account.addressBook.form.state.placeholder'),
          options: usStates,
          value: {},
          defaultValue: {},
          titleField: 'name',
          valueField: 'isocode',
          title: 'region',
          type: 'text',
          label: 'name',
          variant: 'shadow',
          inline: true,
          errors: {
            required: this.$t('account.addressBook.form.state.requiredError')
          },
          disabled: false
        },
        postalCode: {
          placeholder: this.$t('account.addressBook.form.postalCode.placeholder'),
          value: '',
          defaultValue: '',
          title: 'postalCode',
          type: 'text',
          maxLength: '10',
          variant: 'shadow',
          inline: true,
          errors: {
            required: this.$t('account.addressBook.form.postalCode.requiredError')
          },
          disabled: false
        },
        country: {
          title: 'country',
          value: {
            isocode: 'US',
            name: 'United States'
          },
          defaultValue: {
            isocode: 'US',
            name: 'United States'
          },
          disabled: false
        },
        phone: {
          inputType: 'the-mask',
          placeholder: this.$t('account.addressBook.form.phone.placeholder'),
          value: '',
          defaultValue: '',
          title: 'phone',
          type: 'tel',
          mask: '###-###-####',
          inline: true,
          variant: 'shadow',
          errors: {
            required: this.$t('account.addressBook.form.phone.requiredError'),
            numeric: this.$t('account.addressBook.form.phone.requiredError'),
            minLength: this.$t('account.addressBook.form.phone.requiredError')
          },
          disabled: false
        }
      }
    }
  }
}
